import { css } from 'lit';
export default css `
    :host {
        --color: inherit;
        --color-heading: var(--color-heading-primary);
        --font-family: var(--font-normal);
        --font-size: var(--font-size-s);
        --font-weight: normal;
        --line-height: 1.375;
        --margin: 0; /* Remove external margins from the text elements */

        display: block; /* So we can use margins and padding outside the element */
        hyphens: auto;
    }

    :host([tag='span']) {
        display: inline;
    }

    /**
     * DESIGN TOKEN-BASED STYLES
     */

    /* Heading level 1 */
    :host([tag='h1']:not([style-as])),
    :host([style-as='h1']) {
        --color: var(--color-heading);
        --font-family: var(--font-special);
        --font-size: var(--font-size-xxl);
        --line-height: var(--line-height-xxl);
        --font-weight: var(--weight-medium);
    }

    :host([form-factor='desktop'][tag='h1']:not([style-as])),
    :host([form-factor='desktop'][style-as='h1']) {
        --font-size: var(--font-size-xxxl);
        --line-height: var(--line-height-xxxl);
    }

    /* Heading level 2 */
    :host([tag='h2']:not([style-as])),
    :host([style-as='h2']) {
        --color: var(--color-heading);
        --font-family: var(--font-special);
        --font-size: var(--font-size-xl);
        --line-height: var(--line-height-l);
        --font-weight: var(--weight-medium);
    }

    :host([form-factor='desktop'][tag='h2']:not([style-as])),
    :host([form-factor='desktop'][style-as='h2']) {
        --font-size: var(--font-size-xxl);
        --line-height: var(--line-height-xl);
    }

    /* Heading level 3 */
    :host([tag='h3']:not([style-as])),
    :host([style-as='h3']) {
        --color: var(--color-heading);
        --font-family: var(--font-special);
        --font-size: var(--font-size-l);
        --line-height: var(--line-height-m);
        --font-weight: var(--weight-medium);
    }

    :host([form-factor='desktop'][tag='h3']:not([style-as])),
    :host([form-factor='desktop'][style-as='h3']) {
        --font-size: var(--font-size-xl);
        --line-height: var(--line-height-l);
    }

    /* Heading level 4 */
    :host([tag='h4']:not([style-as])),
    :host([style-as='h4']) {
        --color: var(--color-heading);
        --font-family: var(--font-special);
        --font-size: var(--font-size-m);
        --line-height: var(--line-height-s);
        --font-weight: var(--weight-medium);
    }

    :host([form-factor='desktop'][tag='h4']:not([style-as])),
    :host([form-factor='desktop'][style-as='h4']) {
        --font-size: var(--font-size-l);
        --line-height: var(--line-height-m);
    }

    /* Heading level 5 */
    :host([tag='h5']:not([style-as])),
    :host([style-as='h5']) {
        --color: var(--color-heading);
        --font-family: var(--font-normal);
        --font-size: var(--font-size-m);
        --line-height: var(--line-height-s);
        --font-weight: var(--weight-bold);
    }

    /* Heading level 6 */
    :host([tag='h6']:not([style-as])),
    :host([style-as='h6']) {
        --color: var(--color-heading);
        --font-family: var(--font-normal);
        --font-size: var(--font-size-s);
        --line-height: var(--line-height-xs);
        --font-weight: var(--weight-medium);
    }

    /* Body copy */
    :host([tag='p']:not([style-as])),
    :host([tag='span']:not([style-as])),
    :host([style-as='body']) {
        --font-family: var(--font-normal);
        --font-size: var(--font-size-s);
        --line-height: var(--line-height-xs);
        --font-weight: var(--weight-normal);
    }

    /* Body copy, bold */
    :host([style-as='body-bold']) {
        --font-weight: var(--weight-bold);
    }

    /* Body copy, small */
    :host([style-as='body-small']) {
        --font-family: var(--font-normal);
        --font-size: var(--font-size-xxs);
        --line-height: var(--line-height-xs);
        --font-weight: var(--weight-bold);
    }

    /* Theme: blue */
    :host([theme='blue']) {
        --color: var(--color-ui-08);
        --color-heading: var(--color-ui-08);
    }

    /**
     * LEGACY STYLES — remove once all uses of the corresponding attributes have been updated to use tokens
     */

    /* Colors */

    :host([color='heading']) {
        --color: var(--color-heading) !important;
    }

    :host([color='dark']) {
        --color: var(--color-text) !important;
    }

    :host([color='error']) {
        --color: var(--color-error) !important;
    }

    :host([color='light']) {
        --color: var(--color-ui-03) !important;
    }

    :host([color='inherit']) {
        --color: inherit !important;
    }

    /* Font families */

    :host([font='normal']) {
        --font-family: var(--font-normal) !important;
    }

    :host([font='number']) {
        --font-family: var(--font-normal) !important;
        font-variant-numeric: tabular-nums !important;
    }

    :host([font='special']) {
        --font-family: var(--font-special) !important;
    }

    /* Font sizes */

    :host([size='xxxl']) {
        --font-size: var(--font-size-xxxl) !important;
        --line-height: var(--line-height-xxxl) !important;
    }

    :host([size='xxl']) {
        --font-size: var(--font-size-xxl) !important;
        --line-height: var(--line-height-xl) !important;
    }

    :host([size='xl']) {
        --font-size: var(--font-size-xl) !important;
        --line-height: var(--line-height-l) !important;
    }

    :host([size='l']) {
        --font-size: var(--font-size-l) !important;
        --line-height: var(--line-height-m) !important;
    }

    :host([size='m']) {
        --font-size: var(--font-size-m) !important;
        --line-height: var(--line-height-s) !important;
    }

    :host([size='s']) {
        --font-size: var(--font-size-s) !important;
        --line-height: var(--line-height-xs) !important;
    }

    :host([size='xs']) {
        --font-size: var(--font-size-xs) !important;
        --line-height: var(--line-height-xxs) !important;
    }

    :host([size='xxs']) {
        --font-size: var(--font-size-xxs) !important;
    }

    :host([size='xxxs']) {
        --font-size: var(--font-size-xxxs) !important;
    }

    :host([size='xxxxs']) {
        --font-size: var(--font-size-xxxxs) !important;
    }

    /* Line-heights */

    :host([line-height='normal']) {
        --line-height: 1.375 !important;
    }

    :host([line-height='large']) {
        --line-height: 1.5 !important;
    }

    /* Font-weight */

    :host([weight='normal']) {
        --font-weight: var(--weight-normal) !important;
    }

    :host([weight='medium']) {
        --font-weight: var(--weight-medium) !important;
    }

    :host([weight='bold']) {
        --font-weight: var(--weight-bold) !important;
    }

    /* Responsive */

    @media screen and (max-width: 767px) {
        :host([size='xxxl'][responsive]) {
            --font-size: var(--font-size-xxl) !important;
            --line-height: var(--line-height-xxl) !important;
        }

        :host([size='xxl'][responsive]) {
            --font-size: var(--font-size-xl) !important;
            --line-height: var(--line-height-l) !important;
        }

        :host([size='xl'][responsive]) {
            --font-size: var(--font-size-l) !important;
            --line-height: var(--line-height-m) !important;
        }

        :host([size='l'][responsive]) {
            --font-size: var(--font-size-m) !important;
            --line-height: var(--line-height-s) !important;
        }

        :host([size='m'][responsive]) {
            --font-size: var(--font-size-s) !important;
            --line-height: var(--line-height-xs) !important;
        }

        :host([size='s'][responsive]) {
            --font-size: var(--font-size-xs) !important;
            --line-height: var(--line-height-xxs) !important;
        }

        :host([size='xs'][responsive]) {
            --font-size: var(--font-size-xs) !important;
        }

        :host([size='xxs'][responsive]) {
            --font-size: var(--font-size-xxs) !important;
        }

        :host([size='xxxs'][responsive]) {
            --font-size: var(--font-size-xxxs) !important;
        }
    }

    /* Implementation */

    :host,
    :host > * {
        color: var(--color);
        font-family: var(--font-family);
        font-size: var(--font-size);
        font-weight: var(--font-weight);
    }

    :host > * {
        line-height: var(--line-height);
        margin: var(--margin);
    }

    :host([ellipsis]) {
        overflow: hidden;
    }

    :host([ellipsis]) > * {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
`;
